import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { OverlayModule } from '@angular/cdk/overlay';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { LayoutModule } from '@angular/cdk/layout';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { HeaderComponent } from './components/header/header.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { CustomTooltipDirective } from './directives/custom-tooltip.directive';
import { TruncateWithTooltipDirective } from './directives/truncate-with-tooltip.directive';
import { FooterComponent } from './components/footer/footer.component';
import { LoaderComponent } from './components/loader/loader.component';
import { LanguageSwitcherComponent } from './components/language-switcher/language-switcher.component';
import { InlineTranslatorComponent } from './components/inline-translator/inline-translator.component';
import { TranslatePipe } from './pipes/translate.pipe';
import { TranslateLangPipe } from './pipes/translate-lang.pipe';
import { FilenameExtractorPipe } from './pipes/filename-extractor.pipe';
import { TruncateFilenamePipe } from './pipes/truncate-filename.pipe';
import { AddressFormatPipe } from './pipes/address-format.pipe';
import { NumberFormatPipe } from './pipes/number-format.pipe';
import { DateFormatPipe } from './pipes/date-format.pipe';
import { DatePatternPipe } from './pipes/date-pattern.pipe';
import { ButtonComponent } from './components/button/button.component';
import { BackButtonComponent } from './components/back-button/back-button.component';
import { StepperComponent } from './components/stepper/stepper.component';
import { AlertComponent } from './components/alert/alert.component';
import { HeroSectionComponent } from './components/hero-section/hero-section.component';
import { AboutUsComponent } from './components/about-us/about-us.component';
import { FeaturesSectionComponent } from './components/features-section/features-section.component';
import { FeaturedCardComponent } from './components/featured-card/featured-card.component';
import { FaqComponent } from './components/faq/faq.component';
import { SearchFieldComponent } from './components/search-field/search-field.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { SelectComponent } from './components/select/select.component';
import { SearchableSingleSelectComponent } from './components/searchable-single-select/searchable-single-select.component';
import { InputComponent } from './components/input/input.component';
import { AmountCurrencyFieldComponent } from './components/amount-currency-field/amount-currency-field.component';
import { ClosingCtaComponent } from './components/closing-cta/closing-cta.component';
import { ChampionsTestimonialComponent } from './components/champions-testimonial/champions-testimonial.component';
import { HowItWorksComponent } from './components/how-it-works/how-it-works.component';
import { GivingSpaceBlockComponent } from './components/giving-space-block/giving-space-block.component';
import { GivingInfoDialogComponent } from './components/giving-info-dialog/giving-info-dialog.component';
import { FundSwitcherComponent } from './components/fund-switcher/fund-switcher.component';
import { DonateButtonPreviewComponent } from './components/donate-button-preview/donate-button-preview.component';
import { CurrencyIndicatorComponent } from './components/currency-indicator/currency-indicator.component';
import { FundOverviewComponent } from './components/fund-overview/fund-overview.component';
import { CaretIconComponent } from './components/caret-icon/caret-icon.component';
import { CriteriaSelectorComponent } from './components/criteria-selector/criteria-selector.component';
import { CatalogFiltersComponent } from './components/catalog-filters/catalog-filters.component';
import { ActionCardComponent } from './components/action-card/action-card.component';
import { FundActionsComponent } from './components/fund-actions/fund-actions.component';
import { ShareDialogComponent } from './components/share-dialog/share-dialog.component';
import { BadgeComponent } from './components/badge/badge.component';
import { CustomDateRangeComponent } from './components/custom-date-range/custom-date-range.component';
import { ContentCardComponent } from './components/content-card/content-card.component';
import { CurrencyManagementComponent } from './components/currency-management/currency-management.component';
import { SettingsSideNavComponent } from './components/settings-side-nav/settings-side-nav.component';
import { SettingsSideNavSectionComponent } from './components/settings-side-nav-section/settings-side-nav-section.component';
import { SettingCardComponent } from './components/setting-card/setting-card.component';
import { LoadingOverlayComponent } from './components/loading-overlay/loading-overlay.component';
import { IconComponent } from './components/icon/icon.component';
import { LinkOutComponent } from './components/link-out/link-out.component';
import { FundCashSummaryComponent } from './components/fund-cash-summary/fund-cash-summary.component';
import { LoadingErrorComponent } from './components/loading-error/loading-error.component';
import { CharityRowComponent } from './components/charity-row/charity-row.component';
import { CharityProfileCardComponent } from './components/charity-profile-card/charity-profile-card.component';
import { ProjectProfileCardComponent } from './components/project-profile-card/project-profile-card.component';
import { ProjectOtherCardsComponent } from './components/project-other-cards/project-other-cards.component';
import { CharityExplorerComponent } from './components/charity-explorer/charity-explorer.component';
import { SwitchBoardComponent } from './components/switch-board/switch-board.component';
import { PromotionalToolsCardComponent } from './components/promotional-tools-card/promotional-tools-card.component';
import { DonateButtonsDialogComponent } from './components/donate-buttons-dialog/donate-buttons-dialog.component';
import { BeAChampionDialogComponent } from './components/be-a-champion-dialog/be-a-champion-dialog.component';
import { MediaUploadControlComponent } from './components/media-upload-control/media-upload-control.component';
import { AddLanguageDialogComponent } from './components/add-language-dialog/add-language-dialog.component';
import { ImageCropperComponent } from './components/image-cropper/image-cropper.component';
import { DeclineReasonDialogComponent } from './components/decline-reason-dialog/decline-reason-dialog.component';
import { ResubmitChampionDialogComponent } from './components/resubmit-champion-dialog/resubmit-champion-dialog.component';
import { ArchiveConfirmationDialogComponent } from './components/archive-confirmation-dialog/archive-confirmation-dialog.component';
import { DeleteConfirmationDialogComponent } from './components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { AddedUserCardComponent } from './components/added-user-card/added-user-card.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { LanguageSelectionDialogComponent } from './components/language-selection-dialog/language-selection-dialog.component';
import { ToastService } from './services/toast.service';
import { RadioInputComponent } from './components/radio-input/radio-input.component';
import { FormFieldErrorComponent } from './components/form-field-error/form-field-error.component';
import { CurrencyInputDirective } from './directives/currency-input.directive';
import { AmountCurrencyInputComponent } from './components/amount-currency-input/amount-currency-input.component';
import { DonorLoginComponent } from './components/donor-login/donor-login.component';
import { EditUserDialogComponent } from './components/edit-user-dialog/edit-user-dialog.component';
import { ViewChampionFormDialogComponent } from './components/view-champion-form-dialog/view-champion-form-dialog.component';
import { ChampionDeclineDialogComponent } from './components/champion-decline-dialog/champion-decline-dialog.component';
import { DeclineDialogComponent } from './components/decline-dialog/decline-dialog.component';
import { CharityFormComponent } from './components/charity-form/charity-form.component';
import { ProjectFormComponent } from './components/project-form/project-form.component';
import { TeamMemberCardComponent } from './components/team-member-card/team-member-card.component';
import { UnableToProceedDialogComponent } from './components/unable-to-proceed-dialog/unable-to-proceed-dialog.component';
import { MonthDayDatepickerHeaderComponent } from './components/charity-form/month-day-datepicker-header.component';
import { MediaPreviewDialogComponent } from './components/media-preview-dialog/media-preview-dialog.component';
import { MultiSelectDropdownComponent } from './components/multi-select-dropdown/multi-select-dropdown.component';
import { ProfilesTableComponent } from './components/profiles-table/profiles-table.component';
import { GlobalSettingsListComponent } from './components/global-settings-list/global-settings-list.component';
import { GalleryUploadZoneComponent } from './components/gallery-upload-zone/gallery-upload-zone.component';
import { SupportTopicsComponent } from './components/support-topics/support-topics.component';
import { HelpVideosComponent } from './components/help-videos/help-videos.component';
import { HelpVideoPlayerDialogComponent } from './components/help-videos/help-video-player-dialog/help-video-player-dialog.component';

@NgModule({
  declarations: [
    HeaderComponent,
    FooterComponent,
    LoaderComponent,
    ButtonComponent,
    BackButtonComponent,
    StepperComponent,
    AlertComponent,
    AboutUsComponent,
    FeaturesSectionComponent,
    FeaturedCardComponent,
    FaqComponent,
    HeroSectionComponent,
    SearchFieldComponent,
    SelectComponent,
    SearchableSingleSelectComponent,
    InputComponent,
    AmountCurrencyFieldComponent,
    ClosingCtaComponent,
    ChampionsTestimonialComponent,
    HowItWorksComponent,
    GivingSpaceBlockComponent,
    GivingInfoDialogComponent,
    TooltipComponent,
    CustomTooltipDirective,
    TruncateWithTooltipDirective,
    FundSwitcherComponent,
    DonateButtonPreviewComponent,
    CurrencyIndicatorComponent,
    FundOverviewComponent,
    ActionCardComponent,
    FundActionsComponent,
    ShareDialogComponent,
    BadgeComponent,
    CustomDateRangeComponent,
    ContentCardComponent,
    CurrencyManagementComponent,
    SettingsSideNavComponent,
    SettingsSideNavSectionComponent,
    SettingCardComponent,
    LoadingOverlayComponent,
    FundCashSummaryComponent,
    LoadingErrorComponent,
    CharityRowComponent,
    CharityProfileCardComponent,
    ProjectProfileCardComponent,
    ProjectOtherCardsComponent,
    CharityExplorerComponent,
    SwitchBoardComponent,
    PromotionalToolsCardComponent,
    DonateButtonsDialogComponent,
    BeAChampionDialogComponent,
    MediaUploadControlComponent,
    AddLanguageDialogComponent,
    ImageCropperComponent,
    DeclineReasonDialogComponent,
    ResubmitChampionDialogComponent,
    ArchiveConfirmationDialogComponent,
    DeleteConfirmationDialogComponent,
    RadioInputComponent,
    FormFieldErrorComponent,
    DonorLoginComponent,
    AddedUserCardComponent,
    ConfirmationDialogComponent,
    LanguageSelectionDialogComponent,
    EditUserDialogComponent,
    ViewChampionFormDialogComponent,
    ChampionDeclineDialogComponent,
    DeclineDialogComponent,
    CharityFormComponent,
    ProjectFormComponent,
    UnableToProceedDialogComponent,
    MediaPreviewDialogComponent,
    MultiSelectDropdownComponent,
    ProfilesTableComponent,
    GlobalSettingsListComponent,
    GalleryUploadZoneComponent,
    TeamMemberCardComponent,
    SupportTopicsComponent,
    HelpVideosComponent,
    HelpVideoPlayerDialogComponent,
  ],
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatTabsModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    OverlayModule,
    ClipboardModule,
    LayoutModule,
    DragDropModule,
    LanguageSwitcherComponent,
    InlineTranslatorComponent,
    TranslatePipe,
    TranslateLangPipe,
    FilenameExtractorPipe,
    TruncateFilenamePipe,
    AddressFormatPipe,
    NumberFormatPipe,
    DateFormatPipe,
    DatePatternPipe,
    DropdownComponent,
    CaretIconComponent,
    CriteriaSelectorComponent,
    CatalogFiltersComponent,
    IconComponent,
    LinkOutComponent,
    CurrencyInputDirective,
    AmountCurrencyInputComponent,
    MonthDayDatepickerHeaderComponent
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    HeaderComponent,
    FooterComponent,
    LoaderComponent,
    ButtonComponent,
    BackButtonComponent,
    StepperComponent,
    AlertComponent,
    HeroSectionComponent,
    AboutUsComponent,
    FeaturesSectionComponent,
    FeaturedCardComponent,
    FaqComponent,
    ClosingCtaComponent,
    ChampionsTestimonialComponent,
    HowItWorksComponent,
    SearchFieldComponent,
    SelectComponent,
    SearchableSingleSelectComponent,
    InputComponent,
    AmountCurrencyFieldComponent,
    AmountCurrencyInputComponent,
    DropdownComponent,
    TranslatePipe,
    TranslateLangPipe,
    FilenameExtractorPipe,
    TruncateFilenamePipe,
    AddressFormatPipe,
    NumberFormatPipe,
    DateFormatPipe,
    DatePatternPipe,
    GivingSpaceBlockComponent,
    GivingInfoDialogComponent,
    CustomTooltipDirective,
    TruncateWithTooltipDirective,
    FundSwitcherComponent,
    DonateButtonPreviewComponent,
    CurrencyIndicatorComponent,
    FundOverviewComponent,
    CaretIconComponent,
    CriteriaSelectorComponent,
    CatalogFiltersComponent,
    ActionCardComponent,
    FundActionsComponent,
    ShareDialogComponent,
    BadgeComponent,
    CustomDateRangeComponent,
    ContentCardComponent,
    CurrencyManagementComponent,
    SettingsSideNavComponent,
    SettingsSideNavSectionComponent,
    SettingCardComponent,
    LoadingOverlayComponent,
    IconComponent,
    LinkOutComponent,
    FundCashSummaryComponent,
    LoadingErrorComponent,
    CharityRowComponent,
    CharityProfileCardComponent,
    ProjectProfileCardComponent,
    ProjectOtherCardsComponent,
    CharityExplorerComponent,
    SwitchBoardComponent,
    PromotionalToolsCardComponent,
    DonateButtonsDialogComponent,
    BeAChampionDialogComponent,
    MediaUploadControlComponent,
    AddLanguageDialogComponent,
    ImageCropperComponent,
    DeclineReasonDialogComponent,
    ResubmitChampionDialogComponent,
    ArchiveConfirmationDialogComponent,
    DeleteConfirmationDialogComponent,
    RadioInputComponent,
    FormFieldErrorComponent,
    CurrencyInputDirective,
    DonorLoginComponent,
    AddedUserCardComponent,
    ConfirmationDialogComponent,
    LanguageSelectionDialogComponent,
    EditUserDialogComponent,
    ViewChampionFormDialogComponent,
    ChampionDeclineDialogComponent,
    DeclineDialogComponent,
    CharityFormComponent,
    ProjectFormComponent,
    UnableToProceedDialogComponent,
    MediaPreviewDialogComponent,
    MultiSelectDropdownComponent,
    ProfilesTableComponent,
    GlobalSettingsListComponent,
    GalleryUploadZoneComponent,
    TeamMemberCardComponent,
    SupportTopicsComponent,
    HelpVideosComponent,
  ],
})
export class SharedModule {}
